import { useNewVersionAvailable } from "@components/newVersion/NewVersionAvailableContext";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useSideNavMode } from "@design-system/SideNav/SideNavProvider";
import {
    AddMemberIcon,
    ChangelogIcon,
    CheckIcon,
    CogwheelIcon,
    GLIcon,
    PlayIcon,
} from "@icons";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { useHelpCenterUrl } from "@hooks/useHelpCenterUrl";
import { useOrganizationDemoWorkspace } from "@hooks/useOrganizationDemoWorkspace";
import { HelpMenu } from "../HelpMenu";
import {
    CHANGELOG_URL,
    HELP_CENTER_URL,
    KICK_STATUS_URL,
} from "../helpMenuLinks";

export interface SideNavHelpMenuProps {
    /** Omit when the user can't invite at this level. */
    onAddMember?: () => void;
    /** The `onAddMember` item's label; "Add member" by default. */
    addMemberLabel?: string;
    /** Omit when there is no settings page to open at this level. */
    onOpenSettings?: () => void;
}

/**
 * The side nav footer `?`: support, docs and status links, then the level's
 * member and settings actions. In a mini nav, which has no room for the
 * footer's new-version notice, it carries that notice instead.
 */
export const SideNavHelpMenu = ({
    onAddMember,
    addMemberLabel = "Add member",
    onOpenSettings,
}: SideNavHelpMenuProps) => {
    const docsUrl = useHelpCenterUrl() ?? HELP_CENTER_URL;
    const { demoWorkspace, openDemoWorkspace } = useOrganizationDemoWorkspace();
    const { isNoticeVisible, refresh } = useNewVersionAvailable();
    const isMini = useSideNavMode() === "mini";
    const hasLevelActions =
        onAddMember !== undefined || onOpenSettings !== undefined;

    return (
        <HelpMenu
            onNewVersion={isNoticeVisible && isMini ? refresh : undefined}
        >
            <Menu.LinkItem href={docsUrl} target="_blank" leading={<GLIcon />}>
                Docs
            </Menu.LinkItem>
            {demoWorkspace && (
                <Menu.Item
                    leading={<PlayIcon />}
                    onClick={() =>
                        openDemoWorkspace(
                            OrganizationMemberClientAccessSource.OTHER,
                        )
                    }
                >
                    Demo client
                </Menu.Item>
            )}
            <Menu.LinkItem
                href={CHANGELOG_URL}
                target="_blank"
                leading={<ChangelogIcon />}
            >
                Changelog
            </Menu.LinkItem>
            <Menu.LinkItem
                href={KICK_STATUS_URL}
                target="_blank"
                leading={<CheckIcon />}
            >
                Status
            </Menu.LinkItem>
            {hasLevelActions && <Menu.Separator />}
            {onAddMember && (
                <Menu.Item leading={<AddMemberIcon />} onClick={onAddMember}>
                    {addMemberLabel}
                </Menu.Item>
            )}
            {onOpenSettings && (
                <Menu.Item leading={<CogwheelIcon />} onClick={onOpenSettings}>
                    Settings
                </Menu.Item>
            )}
        </HelpMenu>
    );
};
